import {
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  InputAdornment,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";

import FlexBetween from "components/FlexBetween";
import SnackbarComponent from "components/SnackbarComponent";
import { resetPassword } from "reduxStore/actions/authActions";
import { setSnackbar } from "reduxStore/state";

const resetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .max(25, "Password must be at most 25 characters")
    .required("Password is required")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&-])[A-Za-z\d@$!%*?&-]{6,25}$/,
      "Password must include at least one uppercase letter, one lowercase letter, one digit, and one special character (@$!%*?&-)."
    ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

const validatePassword = (password) => ({
  hasMinLength: password.length >= 6,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /\d/.test(password),
  hasSpecialChar: /[@$!%*?&-]/.test(password),
});

function ResetPasswordForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useParams(); // Token from the URL

  const isLargeScreen = useMediaQuery("(min-width:1000px)");

  const [passwordChecklist, setPasswordChecklist] = useState({
    hasMinLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const handlePasswordChange = (password) => {
    setPasswordChecklist(validatePassword(password));
  };

  const handleResetPassword = async (values) => {
    try {
      const response = await dispatch(resetPassword(token, values.password));

      if (response.success) {
        dispatch(
          setSnackbar({
            open: true,
            message: "Password reset successfully. Redirecting to login...",
            severity: "success",
          })
        );
        setTimeout(() => navigate("/"), 4000);
      } else {
        dispatch(
          setSnackbar({
            open: true,
            message: response.message || "Failed to reset password.",
            severity: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        setSnackbar({
          open: true,
          message: error.message || "Error resetting password. Try again.",
          severity: "error",
        })
      );
    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const passwordTooltip =
    "Password must be 6-25 characters long, with at least one uppercase letter, one lowercase letter, one digit, and one special character (@$!%*?&-).";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        width="100%"
        backgroundColor="rgba(255, 255, 255, 0.8)"
        p="1rem 6%"
        textAlign="center"
      >
        <Typography color="primary" variant="h1" fontWeight="bold">
          Deovise
        </Typography>
      </Box>

      <Box
        width={isLargeScreen ? "50%" : "93%"}
        p="2rem"
        m="2rem auto"
        borderRadius="1.5rem"
        backgroundColor="rgba(255, 255, 255, 0.9)"
        boxShadow={3}
      >
        <Typography fontWeight="500" variant="h5" sx={{ mb: "1.5rem" }}>
          Reset Your Password
        </Typography>

        <Formik
          initialValues={{ password: "", confirmPassword: "" }}
          validationSchema={resetPasswordSchema}
          onSubmit={handleResetPassword}
        >
          {({ values, errors, touched, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Box>
                <Tooltip title={passwordTooltip} arrow placement="right-start">
                  <TextField
                    label="New Password"
                    name="password"
                    value={values.password}
                    onChange={(e) => {
                      handleChange(e);
                      handlePasswordChange(e.target.value);
                    }}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? (
                              <VisibilityOffOutlined />
                            ) : (
                              <VisibilityOutlined />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Tooltip>

                <TextField
                  label="Confirm New Password"
                  name="confirmPassword"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  error={
                    touched.confirmPassword && Boolean(errors.confirmPassword)
                  }
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  margin="normal"
                />

                <FlexBetween>
                  {/* Password Checklist */}
                  <List>
                    {[
                      {
                        label: "At least 6 characters",
                        check: passwordChecklist.hasMinLength,
                      },
                      {
                        label: "One uppercase letter",
                        check: passwordChecklist.hasUppercase,
                      },
                      {
                        label: "One lowercase letter",
                        check: passwordChecklist.hasLowercase,
                      },
                      {
                        label: "One number",
                        check: passwordChecklist.hasNumber,
                      },
                      {
                        label: "One special character (@$!%*?&-)",
                        check: passwordChecklist.hasSpecialChar,
                      },
                    ].map(({ label, check }) => (
                      <ListItem
                        key={label}
                        sx={{ paddingTop: 0, paddingBottom: 0 }}
                      >
                        <FormControlLabel
                          control={<Checkbox checked={check} color="primary" />}
                          label={label}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button type="submit" variant="contained" color="primary">
                    Reset Password
                  </Button>
                </FlexBetween>
              </Box>
            </form>
          )}
        </Formik>
      </Box>

      <SnackbarComponent />
    </Box>
  );
}

export default ResetPasswordForm;
